import queryString from 'query-string';
import { useCallback, useEffect, useState } from 'react';

import { useUserEnvironment } from 'contexts/UserEnvironmentContext';
import type { CmsApiSearchLandingPage } from 'modules/cms/api/types/CmsApiSearchLandingPage';
import { searchTrackDisplayedListings } from 'modules/search/helpers/searchTrackDisplayedListings';
import { assignSearchPageUrlParametersToZustandStore } from 'modules/search/pages/Search/assignSearchPageUrlParametersToZustandStore';
import type { SearchFacet } from 'modules/search/types/SearchFacet';
import {
  fetchSearchPageResults,
  updateSearchLocation,
} from 'modules/search/zustand-stores/searchStore';
import { EMPTY_SEARCH_LOCATION } from 'utils/constants/general/emptySearchLocation';

type Args = {
  content: CmsApiSearchLandingPage;
  isSsr: boolean;
  getSearchTrackingData: () => Record<string, unknown>;
  searchFacets: SearchFacet[];
};

export function useSearchLandingPageLifecycle({
  content,
  isSsr,
  getSearchTrackingData,
  searchFacets,
}: Args) {
  const { isEnvironmentLoaded } = useUserEnvironment();

  const [initialized, setInitialized] = useState(isSsr);

  const initialize = useCallback(async () => {
    assignSearchPageUrlParametersToZustandStore({
      searchParams: new URLSearchParams(
        queryString.stringify({
          ...content.filters,
          radius: content.location.radius,
          q: content.searchText,
        }),
      ),
      searchType: content.filters.type,
      searchFacets,
    });

    updateSearchLocation(
      content.location?.searchLocation || EMPTY_SEARCH_LOCATION,
      false,
    );

    const results = await fetchSearchPageResults();
    searchTrackDisplayedListings(results.hits, getSearchTrackingData());

    setInitialized(true);
  }, [
    content.filters,
    content.location,
    content.searchText,
    getSearchTrackingData,
    searchFacets,
  ]);

  useEffect(() => {
    // Usually the user "lands" on the landing page, which has been initialized
    // through or backend.
    if (!isEnvironmentLoaded || initialized) return;

    // But in the case where the user goes to a different page and comes back, we
    // still need to initialize it
    initialize();
  }, [isEnvironmentLoaded, initialize, initialized]);

  return { initialized };
}
